import "../../styles/customerRecord/customerRecord.css";
import React, { useState } from "react";
import { getIcon } from "../../utils/iconUtils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { format } from "date-fns";
import { validateUserPermission } from "../../utils/helpers";
import { getTranslation } from "../../common/translation";
import { useTranslation } from "react-i18next";
import DeskingSheet from "./DeskingSheet";
import { PDFExport } from "@progress/kendo-react-pdf";

const FinanceRecord = ({
  marketSettings,
  financeData,
  customerRecordData,
  clientPositionData,
  currentUser,
  status,
  pdfExportComponent,
  exportDeskingSheet
}) => {
  const { t } = useTranslation();
  const [showNewUsedDropdown, setShowNewUsedDropdown] = useState(true);
  const [showEquityDropdown, setShowEquityDropdown] = useState(true);
  const [showTotalDepositDropdown, setShowTotalDepositDropdown] =
    useState(true);
  const [showTermKilometerDropdown, setShowTermKilometerDropdown] =
    useState(true);
  const Navigate = useNavigate();
  const { id } = useParams();
  let location = useLocation();
  const formatCurrency = (value, symbol, separator) => {
    return value
      ? `${symbol}${Number(value).toLocaleString(undefined, {
          maximumFractionDigits: 2
        })}`
      : null;
  };

  const currencyOptions = {
    symbol: marketSettings.currencySymbol,
    separator: ","
  };

  const financeRecordData = [
    {
      details: getTranslation("Description", t),
      existing: financeData?.existing?.description,
      proposed: financeData?.proposed?.description
    },
    {
      details: getTranslation("New/Used", t),
      existing: financeData?.existing?.newOrUsed,
      proposed: financeData?.proposed?.newOrUsed
    },
    {
      details: getTranslation("Registration date", t),
      existing: financeData?.existing?.registrationDate
        ? format(
            new Date(financeData?.existing?.registrationDate),
            "dd/MM/yyyy"
          )
        : null,
      proposed: "N/A"
    },
    {
      details: getTranslation("Start date", t),
      existing: financeData?.existing?.startDate
        ? format(new Date(financeData?.existing?.startDate), "dd/MM/yyyy")
        : null,
      proposed: "N/A"
    },
    {
      details: getTranslation("Start kilometers", t),
      existing: financeData?.existing?.startKilometers,
      proposed: financeData?.proposed?.startKilometers
    },
    {
      details: getTranslation("Start age", t),
      existing: financeData?.existing?.startAge,
      proposed: financeData?.proposed?.startAge
    },
    {
      details: getTranslation("License Plate", t),
      existing: financeData?.existing?.licencePlate,
      proposed: financeData?.proposed?.licencePlate
    },
    {
      details: getTranslation("VIN", t),
      existing: financeData?.existing?.vin,
      proposed: financeData?.proposed?.vin
    },
    {
      details: getTranslation("Payments remaining", t),
      existing: financeData?.existing?.paymentsRemaining,
      proposed: "N/A"
    },
    {
      details: getTranslation("Term/Total Km", t),
      existing: "",
      proposed: ""
    },
    {
      details: getTranslation("Term", t),
      existing: financeData?.existing?.term,
      proposed: financeData?.proposed?.term
    },
    {
      details: getTranslation("Total Kilometers", t),
      existing: financeData?.existing?.totalKilometers,
      proposed: financeData?.proposed?.totalKilometers
    },
    {
      details: getTranslation("Finance Type", t),
      existing: financeData?.existing?.financeTypeName,
      proposed: financeData?.proposed?.financeTypeName
    },
    {
      details: getTranslation("Interest rate", t),
      existing: financeData?.existing?.interestRateFormatted
        ? `${financeData?.existing?.interestRateFormatted}%`
        : null,
      proposed: financeData?.proposed?.interestRateFormatted
        ? `${financeData?.proposed?.interestRateFormatted}%`
        : null
    },
    {
      details: getTranslation("Equity", t),
      existing: financeData?.existing?.equityFormatted
        ? formatCurrency(
            financeData?.existing?.equityFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null,
      proposed: "N/A"
    },
    {
      details: getTranslation("Trade-in Value", t),
      existing: financeData?.existing?.tradeInValueFormatted
        ? formatCurrency(
            financeData?.existing?.tradeInValueFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null,
      proposed: "N/A"
    },
    {
      details: getTranslation("Settlement Value", t),
      existing: financeData?.existing?.settlementValueFormatted
        ? formatCurrency(
            financeData?.existing?.settlementValueFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null,
      proposed: "N/A"
    },
    {
      details: getTranslation("Purchased Price", t),
      existing: financeData?.existing?.purchasePriceFormatted
        ? formatCurrency(
            financeData?.existing?.purchasePriceFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null,
      proposed: financeData?.proposed?.purchasePriceFormatted
        ? formatCurrency(
            financeData?.proposed?.purchasePriceFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    },
    {
      details: getTranslation("Total Deposit", t),
      existing: financeData?.existing?.totalDepositFormatted
        ? formatCurrency(
            financeData?.existing?.totalDepositFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null,
      proposed: financeData?.proposed?.totalDepositFormatted
        ? formatCurrency(
            financeData?.proposed?.totalDepositFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    },
    {
      details: getTranslation("Total manufacturer deposit", t),
      existing: "N/A",
      proposed: financeData?.proposed?.totalManufacturerDepositFormatted
        ? formatCurrency(
            financeData?.proposed?.totalManufacturerDepositFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    },
    {
      details: getTranslation("Total dealer deposit", t),
      existing: "N/A",
      proposed: financeData?.proposed?.totalDealerDepositFormatted
        ? formatCurrency(
            financeData?.proposed?.totalDealerDepositFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    },
    {
      details: getTranslation("Total customer deposit", t),
      existing: "N/A",
      proposed: financeData?.proposed?.totalCustomerDepositFormatted
        ? formatCurrency(
            financeData?.proposed?.totalCustomerDepositFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    },
    {
      details: getTranslation("Total amount financed", t),
      existing: financeData?.existing?.totalAmountFinancedFormatted
        ? formatCurrency(
            financeData?.existing?.totalAmountFinancedFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null,
      proposed: financeData?.proposed?.totalAmountFinancedFormatted
        ? formatCurrency(
            financeData?.proposed?.totalAmountFinancedFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    },
    {
      details: getTranslation("Guaranteed Future Value (GFV)", t),
      existing: financeData?.existing?.gfvFormatted
        ? formatCurrency(
            financeData?.existing?.gfvFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null,
      proposed: financeData?.proposed?.gfvFormatted
        ? formatCurrency(
            financeData?.proposed?.gfvFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    },
    {
      details: getTranslation("Monthly payment", t),
      existing: financeData?.existing?.monthlyPaymentFormatted
        ? formatCurrency(
            financeData?.existing?.monthlyPaymentFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null,
      proposed: financeData?.proposed?.monthlyPaymentFormatted
        ? formatCurrency(
            financeData?.proposed?.monthlyPaymentFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    },
    {
      details: getTranslation("Monthly difference", t),
      existing: "N/A",
      proposed: financeData?.proposed?.monthlyDifferenceFormatted
        ? formatCurrency(
            financeData?.proposed?.monthlyDifferenceFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    },
    {
      details: getTranslation("Deposit to match payment/Cashback potential", t),
      existing: "N/A",
      proposed: financeData?.proposed?.cashbackFormatted
        ? formatCurrency(
            financeData?.proposed?.cashbackFormatted,
            currencyOptions.symbol,
            currencyOptions.separator
          )
        : null
    }
  ];

  // Add styling on table rows
  const rowRender = (trElement, props) => {
    const trProps = {
      style: {
        backgroundColor: "#ffff",
        color: "#00233AE5"
      }
    };

    const tdProps = {
      style: {
        borderRightWidth: 0,
        borderLeftWidth: 0,
        borderBottomWidth: 1,
        height: 57
      }
    };
    return React.cloneElement(
      trElement,
      {
        ...trProps
      },
      trElement.props.children.map((tdElement) => {
        return React.cloneElement(
          tdElement,
          {
            ...tdProps
          },
          tdElement.props.children
        );
      })
    );
  };

  // Render table header
  const headerCell = (props) => {
    const { title } = props;

    return (
      <div className="tableHeaderCell">
        <label
          className={
            title === "Proposed"
              ? `${"headerTitle"} ${"proposedHeaderTitle"}`
              : "headerTitle"
          }
        >
          <span>{getTranslation(title, t)}</span>
        </label>
      </div>
    );
  };

  //Render table row
  const rowCell = (props) => {
    const { dataItem, field } = props;
    const { details } = dataItem;
    const newUsedDetails =
      details === getTranslation("Registration date", t) ||
      details === getTranslation("Start date", t) ||
      details === getTranslation("Start kilometers", t) ||
      details === getTranslation("Start age", t);
    const equityDetails =
      details === getTranslation("Trade-in Value", t) ||
      details === getTranslation("Settlement Value", t);
    const totalDepositDetails =
      details === getTranslation("Total manufacturer deposit", t) ||
      details === getTranslation("Total dealer deposit", t) ||
      details === getTranslation("Total customer deposit", t);
    const termTotalKilometerDetails =
      details === getTranslation("Term", t) ||
      details === getTranslation("Total Kilometers", t);

    return (
      //add rows to hide
      <td
        hidden={
          (showNewUsedDropdown && newUsedDetails) ||
          (showEquityDropdown && equityDetails) ||
          (showTotalDepositDropdown && totalDepositDetails) ||
          (showTermKilometerDropdown && termTotalKilometerDetails)
            ? "hidden"
            : ""
        }
        className={
          newUsedDetails ||
          equityDetails ||
          totalDepositDetails ||
          termTotalKilometerDetails
            ? `${"rowCell"} ${"expandedDetails"}`
            : "rowCell"
        }
      >
        <div
          className={
            details === getTranslation("Monthly payment", t) &&
            field !== "existing"
              ? "monthlyPayment"
              : details === getTranslation("Monthly payment", t) &&
                field === "existing"
              ? "monthlyPaymentAmount"
              : field === "proposed"
              ? "proposedRow"
              : field === "details" &&
                (newUsedDetails ||
                  equityDetails ||
                  totalDepositDetails ||
                  termTotalKilometerDetails)
              ? "expandedDetailsRow"
              : "row"
          }
        >
          {field === "details" ? dataItem[field] : dataItem[field]}
          {
            // Add dropdown caret button
            (details === getTranslation("New/Used", t) &&
              field === "details") ||
            (details === getTranslation("Equity", t) && field === "details") ||
            (details === getTranslation("Total Deposit", t) &&
              field === "details") ||
            (details === getTranslation("Term/Total Km", t) &&
              field === "details") ? (
              <button
                className={"caretIconBtn"}
                onClick={() => {
                  handleDropdowns(details);
                }}
              >
                <img
                  src={
                    (showNewUsedDropdown &&
                      details === getTranslation("New/Used", t)) ||
                    (showEquityDropdown &&
                      details === getTranslation("Equity", t)) ||
                    (showTotalDepositDropdown &&
                      details === getTranslation("Total Deposit", t)) ||
                    (showTermKilometerDropdown &&
                      details === getTranslation("Term/Total Km", t))
                      ? getIcon("caret-down.svg")
                      : getIcon("caret-up.svg")
                  }
                  alt="caretIcon"
                />
              </button>
            ) : null
          }
        </div>
      </td>
    );
  };

  //Handles dropdowns in Finance record
  const handleDropdowns = (details) => {
    if (details === getTranslation("New/Used", t)) {
      setShowNewUsedDropdown(!showNewUsedDropdown);
    } else if (details === getTranslation("Equity", t)) {
      setShowEquityDropdown(!showEquityDropdown);
    } else if (details === getTranslation("Total Deposit", t)) {
      setShowTotalDepositDropdown(!showTotalDepositDropdown);
    } else if (details === getTranslation("Term/Total Km", t)) {
      setShowTermKilometerDropdown(!showTermKilometerDropdown);
    }
  };

  return (
    <>
      {validateUserPermission(currentUser.permissions, "canEdit", "Leads") && (
        <button
          onClick={() =>
            Navigate(`/customer-record/${id}/edit-proposed-deal`, {
              state: { prevPage: location.state?.prevPage }
            })
          }
          className="editDeal-btn primary-btn"
          disabled={status === 1}
        >
          {getTranslation("Edit deal", t)}
        </button>
      )}
      {validateUserPermission(
        currentUser.permissions,
        "canCreate",
        "Leads"
      ) && (
        <button
          className="deskingSheet-btn secondary-btn"
          disabled={status === 1}
          onClick={exportDeskingSheet}
        >
          {getTranslation("Desking sheet", t)}
        </button>
      )}
      <div className="grid-container">
        <Grid
          className="financeRecordTableGrid"
          data={financeRecordData}
          rowRender={rowRender}
        >
          <GridColumn
            headerCell={headerCell}
            cell={rowCell}
            field="details"
            title="Details"
            width="200px"
          />
          <GridColumn
            headerCell={headerCell}
            cell={rowCell}
            field="existing"
            title="Existing"
            width="180px"
          />
          <GridColumn
            headerCell={headerCell}
            cell={rowCell}
            field="proposed"
            title="Proposed"
            width="180px"
          />
        </Grid>
      </div>
      <div
        style={{
          position: "absolute",
          left: -1000,
          top: 300,
          zIndex: 10000
        }}
      >
        <div className="desking-sheet">
          <PDFExport
            ref={pdfExportComponent}
            paperSize="Letter"
            margin={{ top: 20, bottom: 20, left: 40, right: 40 }}
            fileName={`${customerRecordData.customerName} ${format(
              new Date(),
              "dd.MM.yyyy HH.mm"
            )}.pdf`}
            author="Retention Engine - Inchcape Digital Delivery Center"
          >
            <DeskingSheet
              marketSettings={marketSettings}
              financeData={financeData}
              currentUser={currentUser}
              status={clientPositionData.status}
              customerRecordData={customerRecordData}
              clientPosition={clientPositionData}
            />
          </PDFExport>
        </div>
      </div>
    </>
  );
};

export default FinanceRecord;
